import * as proto from '../protocol/schemas/ccgames-boardgame_pb'
import {
  DATA_TYPE_ACTIONS_LIST,
  DATA_TYPE_ERROR,
  DATA_TYPE_REQUEST,
} from '../Connector'
import { dict } from './utils'

export const COLOR = dict({
  UNDEFINED: 0,
  WHITE: 2,
  BLACK: 3,
})

export const GAME_STATE = dict({
  UNDEFINED: 0,
  STATE_AWAITING_PLAYERS: 1,
  STATE_ACTIVE: 2,
  STATE_COMPLETE: 3,
})

export const GAME_COMPLETE_RESULT = dict({
  WHITE_WIN: 0,
  BLACK_WIN: 1,
  DRAW: 2,
  NO_RESULT: 3,
})

export const GAME_COMPLETE_RESULT_GENESIS = dict({
  RESERVED: 0,
  CHECKMATE: 1,
  SURRENDER: 2,
  LOSS_ON_TIME: 3,
  NO_PAWNS_OR_MOVES: 4,

  OPPONENT_CONNECTIVITY_ERRORS: 49,
  RULES_VIOLATION: 50,
  ADJUDICATION: 51,

  STALEMATE: 101,
  INSUFFICIENT_MATERIAL: 102,
  FIFTY_MOVES_RULE: 103,
  TRIPLE_REPETITION: 104,
  DRAW_BY_AGREEMENT: 105,
  CONSECUTIVE_QUEEN_MOVES: 106,

  TECHNICAL_ISSUE: 201,
  TECHNICAL_MOVES_LIMIT_REACHED: 202,
  ABANDONED: 203,
})

export const CLOCK_FREEZE_REASON = dict({
  UNDEFINED: 0,
  DISCONNECTED: 1,
  OPPONENT_TURN: 2,
  GAME_NOT_STARTED: 3,
})

export const DRAW_STATE = dict({
  NOT_PROPOSED: 0,
  PROPOSAL_SENT: 1,
  PROPOSAL_RECEIVED: 2,
  ACCEPTED: 3,
  TEMPORARILY_BLOCKED: 4,
})

const _move = (v) => {
  const steps = [{ _beforeMove: null }]

  v.stepsList.forEach((step) => {
    if (step.jumps !== undefined) {
      steps.push({ jumps: step.jumps.fieldIndexesList })
    } else if (step.capture !== undefined) {
      steps.push({ capture: step.capture })
    } else if (step.replace !== undefined) {
      steps.push({ replace: step.replace })
    } else if (step.add !== undefined) {
      steps.push({ add: { [step.add.fieldIndex]: step.add.piece } })
    }
  })

  steps.push({ _afterMove: null })

  return steps
}

const actionTranslators = {
  setGameState: (name, value, adaptedActions) => {
    return {
      yourColor: value.yourColor,
      finalSave: value.finalSave,
      state: value.state,
      stateStr: GAME_STATE._VALUES[value.state],
    }
  },
  initBoard: (name, value, adaptedActions) => {
    const result = {
      boardId: value.boardId,
      startingColor: value.startingColor,
      zeroFieldColor: value.zeroFieldColor,
      playableFieldsIndexes: value.playableFieldIndexesList,
      pieces: value.piecesList,
    }

    return result
  },
  setSideToMove: (name, value, adaptedActions) => {
    return value.sideToMove
  },
  setAvailableMoves: (name, value, adaptedActions) => {
    const result = {}

    value.availableMovesList.forEach((item) => {
      result[item.code] = _move(item)
    })

    return result
  },
  setPlayerClockState: (name, value, adaptedActions) => {
    return {
      playerColor: value.playerColor,
      freeze: value.freeze,
      freezeReason: value.freezeReason,
      freezeReasonStr: CLOCK_FREEZE_REASON._VALUES[value.freezeReason],
      leftMilliseconds: value.leftMilliseconds,
      visible: value.visible,
      receivedAt: Date.now(),
    }
  },
  setFirebasePlayer: (name, value, adaptedActions) => {
    return {
      avatarId: value.avatarId,
      countryCode: value.countryCode,
      firebaseId: value.firebaseId,
      name: value.name,
      playerColor: value.playerColor,
      vip: value.vip,
      anonymous: false,
    }
  },
  setAnonymousPlayer: (name, value, adaptedActions) => {
    return {
      avatarId: null,
      countryCode: null,
      firebaseId: null,
      name: null,
      playerColor: value.playerColor,
      vip: false,
      anonymous: true,
    }
  },
  setDrawState: (name, value, adaptedActions) => {
    return {
      canAcceptOrRejectDraw: value.canAcceptOrRejectDraw,
      canProposeDraw: value.canProposeDraw,
      shouldInformAboutProposalRejection:
        value.shouldInformAboutProposalRejection,
    }
  },
  activateGame: (name, value, adaptedActions) => {
    return value
  },
  move: (name, value, adaptedActions) => {
    //unpack move subactions in place of move action
    const moveActions = _move(value)
    adaptedActions.push(...moveActions)

    return null
  },
  completeGame: (name, value, adaptedActions) => {
    return {
      arbiter: value.finalSave.arbiterGameSave,
      result: value.finalSave.result,
      resultStr: GAME_COMPLETE_RESULT._VALUES[value.finalSave.result],
      genesis: value.finalSave.resultGenesis,
      genesisStr:
        GAME_COMPLETE_RESULT_GENESIS._VALUES[value.finalSave.resultGenesis],
    }
  },
  showSummary: (name, value, adaptedActions) => {
    return value
  },
}

export default class BoardGameProtocolAdapter {
  constructor(socket) {
    this._socket = socket
  }

  confirmReady(nakamaMatchId) {
    console.log('BoardGameProtocolAdapter::confirmReady', nakamaMatchId)

    this._request(nakamaMatchId, {
      setRtsGameConfirmReadiness: new proto.Rts.Request.ConfirmReadiness(),
    })
  }

  sendMove(nakamaMatchId, moveId) {
    console.log('BoardGameProtocolAdapter::sendMove', nakamaMatchId, moveId)

    const makeMove = new proto.Rts.Request.MakeMove()
    makeMove.setMoveCode(moveId)

    this._request(nakamaMatchId, {
      setMakeMove: makeMove,
    })
  }

  proposeDraw(nakamaMatchId) {
    console.log('BoardGameProtocolAdapter::proposeDraw', nakamaMatchId)

    this._request(nakamaMatchId, {
      setProposeDraw: new proto.Rts.DrawExtension.Request.ProposeDraw(),
    })
  }

  acceptDrawProposal(nakamaMatchId) {
    console.log('BoardGameProtocolAdapter::confirmDrawProposal', nakamaMatchId)

    this._request(nakamaMatchId, {
      setAcceptDraw: new proto.Rts.DrawExtension.Request.AcceptDraw(),
    })
  }

  rejectDrawProposal(nakamaMatchId) {
    console.log('BoardGameProtocolAdapter::rejectDrawProposal', nakamaMatchId)

    this._request(nakamaMatchId, {
      setRejectDraw: new proto.Rts.DrawExtension.Request.RejectDraw(),
    })
  }

  surrender(nakamaMatchId) {
    console.log('BoardGameProtocolAdapter::surrender', nakamaMatchId)

    this._request(nakamaMatchId, {
      setSurrender: new proto.Rts.SurrenderExtension.Request.Surrender(),
    })
  }

  _request(nakamaMatchId, fields) {
    const request = new proto.Rts.Request()

    Object.entries(fields).forEach(([name, value]) => {
      request[name](value)
    })

    this._socket.sendMatchState(
      nakamaMatchId,
      proto.Rts.OpCodes.OPCODE_REQUESTS,
      request.serializeBinary()
    )
  }

  onMatchData(matchData) {
    const buffer = matchData.data

    console.log('BoardGameProtocolAdapter::onMatchData', matchData, buffer)

    switch (matchData.op_code) {
      case proto.Rts.OpCodes.OPCODE_ACTIONS:
        return this._handleActions(
          proto.Rts.Actions.deserializeBinary(buffer).toObject()
        )

      case proto.Rts.OpCodes.OPCODE_ERRORS:
        return this._handleError(
          proto.Rts.Error.deserializeBinary(buffer).toObject()
        )

      case proto.Rts.OpCodes.OPCODE_REQUESTS:
        return this._handleRequests()

      default:
        break
    }
  }

  _handleActions(protoActions) {
    console.log('BoardGameProtocolAdapter::_handleActions', protoActions)

    const actions = []

    protoActions.actionsList.forEach((actionsList) => {
      Object.entries(actionsList).forEach(([key, value]) => {
        if (value !== undefined) {
          const action = this._adaptAction(key, value, actions)

          if (action) {
            actions.push(action)
          }
        }
      })
    })

    return { type: DATA_TYPE_ACTIONS_LIST, data: actions }
  }

  _handleError(error) {
    console.log('BoardGameProtocolAdapter::_handleError', error)

    return { type: DATA_TYPE_ERROR, error }
  }

  _handleRequests(requests) {
    console.log('BoardGameProtocolAdapter::_handleRequests', requests)

    return { type: DATA_TYPE_REQUEST }
  }

  _adaptAction(name, value, adaptedActions) {
    if (actionTranslators[name]) {
      const translated = actionTranslators[name](name, value, adaptedActions)

      if (translated === undefined) {
        console.warn(
          'translator for action: ' +
            name +
            ' returned undefined; passing unchanged data:',
          value
        )
      }

      if (translated === null) {
        return null
      }

      return { [name]: translated === undefined ? value : translated }
    } else {
      console.warn('no translator for action: ' + name + ', value:', value)
      return { [name]: value }
    }
  }
}
